import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Grid,
  Paper
} from '@mui/material';
import { shades } from '../../../theme';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import MedicationIcon from '@mui/icons-material/Medication';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

const CTASection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:900px)');

  return (
    <Box
      sx={{
        position: 'relative',
        my: { xs: 8, md: 10 },
        py: { xs: 6, md: 8 },
        overflow: 'hidden',
        background: isDarkMode
          ? `linear-gradient(45deg, ${shades.neutral[900]}, ${shades.neutral[800]})`
          : `linear-gradient(45deg, ${shades.neutral[100]}, ${shades.white[100]})`
      }}
    >
      {/* Background pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          background: `radial-gradient(circle, ${isDarkMode ? shades.white[100] : shades.secondary[500]} 2px, transparent 2px)`,
          backgroundSize: '30px 30px',
          zIndex: 0
        }}
      />

      <Container maxWidth='xl' sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems='center'>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: isDarkMode ? shades.accent[300] : shades.accent[600],
                  mb: 1,
                  letterSpacing: 1
                }}
              >
                Your Health Partner
              </Typography>
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  color: isDarkMode ? shades.white[100] : shades.secondary[700],
                  lineHeight: 1.2
                }}
              >
                Get Premium Medical Care
                <Box component='span' sx={{ color: isDarkMode ? shades.accent[300] : shades.accent[600] }}> Without Leaving Home</Box>
              </Typography>
              <Typography
                variant='body1'
                sx={{
                  mb: 4,
                  color: isDarkMode ? shades.white[300] : shades.secondary[500],
                  maxWidth: '90%',
                  fontSize: { xs: '1rem', md: '1.1rem' }
                }}
              >
                Experience healthcare reimagined with M-Treat. Schedule appointments, order medications,
                and consult with specialists—all from the comfort of your home.
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                <Button
                  variant='contained'
                  size='large'
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/app/patient_login')}
                  sx={{
                    py: 1.5,
                    px: 3,
                    borderRadius: 2,
                    fontWeight: 600,
                    backgroundColor: isDarkMode ? shades.accent[600] : shades.accent[500],
                    color: shades.white[100],
                    boxShadow: isDarkMode
                      ? '0 10px 20px -5px rgba(0, 77, 85, 0.4)'
                      : '0 10px 20px -5px rgba(0, 189, 201, 0.4)',
                    '&:hover': {
                      backgroundColor: isDarkMode ? shades.accent[500] : shades.accent[600],
                      transform: 'translateY(-2px)',
                      boxShadow: isDarkMode
                        ? '0 14px 28px -8px rgba(0, 77, 85, 0.5)'
                        : '0 14px 28px -8px rgba(0, 189, 201, 0.5)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Get Started
                </Button>
                <Button
                  variant='outlined'
                  size='large'
                  startIcon={<LocalPhoneIcon />}
                  onClick={() => navigate('/app/contact')}
                  sx={{
                    py: 1.5,
                    px: 3,
                    borderRadius: 2,
                    fontWeight: 600,
                    borderWidth: 2,
                    borderColor: isDarkMode ? shades.white[100] : shades.secondary[500],
                    color: isDarkMode ? shades.white[100] : shades.secondary[500],
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: isDarkMode ? shades.white[100] : shades.secondary[500],
                      backgroundColor: isDarkMode
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(0,0,0,0.05)'
                    }
                  }}
                >
                  Contact Us
                </Button>
              </Box>
            </Box>
          </Grid>

          {!isTablet && (
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                {[
                  {
                    icon: <MedicationIcon sx={{ fontSize: 40 }} />,
                    title: '24/7 Medication Delivery',
                    description: 'Get your prescriptions delivered to your doorstep any time of day or night.'
                  },
                  {
                    icon: <HealthAndSafetyIcon sx={{ fontSize: 40 }} />,
                    title: 'Virtual Consultations',
                    description: 'Connect with healthcare professionals through secure video consultations.'
                  }
                ].map((feature, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                      <Paper
                          elevation={0}
                          sx={{
                              p: 3,
                              height: '100%',
                              borderRadius: 4,
                              backgroundColor: isDarkMode
                                ? 'rgba(255,255,255,0.05)'
                                : 'rgba(255,255,255,0.8)',
                              backdropFilter: 'blur(10px)',
                              border: `1px solid ${isDarkMode
                                                    ? 'rgba(255,255,255,0.1)'
                                                    : 'rgba(0,0,0,0.05)'}`,
                              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-5px)',
                                boxShadow: `0 10px 30px -10px ${isDarkMode
                                                        ? 'rgba(0,0,0,0.5)'
                                                        : 'rgba(0,0,0,0.15)'}`
                              }
                            }}
                        >
                          <Box
                              sx={{
                                  color: isDarkMode ? shades.accent[300] : shades.accent[600],
                                  mb: 2
                                }}
                            >
                              {feature.icon}
                            </Box>
                          <Typography
                              variant='h6'
                              sx={{
                                  fontWeight: 600,
                                  mb: 1,
                                  color: isDarkMode ? shades.white[100] : shades.secondary[700]
                                }}
                            >
                              {feature.title}
                            </Typography>
                          <Typography
                              variant='body2'
                              sx={{
                                  color: isDarkMode ? shades.white[300] : shades.secondary[500]
                                }}
                            >
                              {feature.description}
                            </Typography>
                        </Paper>
                    </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>

        {/* Stats counter section */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            mt: 6,
            pt: 4,
            borderTop: `1px solid ${isDarkMode
                            ? 'rgba(255,255,255,0.1)'
                            : 'rgba(0,0,0,0.05)'}`
          }}
        >
          {[
            { value: '50,000+', label: 'Happy Patients' },
            { value: '500+', label: 'Doctors' },
            { value: '200+', label: 'Pharmacies' },
            { value: '24/7', label: 'Customer Support' }
          ].map((stat, index) => (
            <Box
              key={index}
              sx={{
                textAlign: 'center',
                flex: { xs: '0 0 50%', sm: '0 0 25%' },
                mb: { xs: 3, sm: 0 }
              }}
            >
              <Typography
                variant='h3'
                sx={{
                  fontWeight: 700,
                  color: isDarkMode ? shades.white[100] : shades.secondary[700],
                  mb: 1
                }}
              >
                {stat.value}
              </Typography>
              <Typography
                variant='body1'
                sx={{
                  color: isDarkMode ? shades.white[300] : shades.secondary[500],
                  fontWeight: 500
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default CTASection;
